import "./AddCategoryForm.css";

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../components/Navbar/Navbar";
import TopHeader from "../components/Navbar/TopHeader";
import { apiCall } from "../api/apiCall";
import generateToastContainer from "../utils/ToastContainer";
import { CategoryLabels } from "../utils/categoryLabels";
import { User } from "../models/user";
import { Company } from "../models/company";
import { useTranslation } from "../i18n/UseTranslation";
import { CategorySelect } from "../components/CategorySelect";
import { CategoryItemType } from "../models/category";
import { getAuth, onAuthStateChanged } from "firebase/auth";


function normalizeCategoryKey(itemType: string) {
  return itemType
    .toLowerCase()
    .replace(/_([a-z])/g, (_, letter: string) =>
      letter.toUpperCase()
    );
}

function AddCategoryForm() {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const translations = t.addCategory;
  const categoryTypes = t.categoryTypes;

  const translation = t.categoryTypes;

  const [itemType, setItemType] =
    useState<string>(translation.standard.toUpperCase() );

  const [selectedCompanyUuid, setSelectedCompanyUuid] =
    useState<string>("");

  const [currentUser, setCurrentUser] =
    useState<User | null>(null);

  const [companies, setCompanies] =
    useState<Company[]>([]);

  const [isSubmitting, setIsSubmitting] =
    useState<boolean>(false);

  const [isLoading, setIsLoading] =
    useState<boolean>(true);

  const categoryOptions = useMemo(
  () =>
    Object.keys(CategoryLabels).map((key) => {
      const translationKey = normalizeCategoryKey(
        key
      ) as keyof typeof categoryTypes;

      return {
        value: key as CategoryItemType,
        label:
          categoryTypes[translationKey] ??
          CategoryLabels[key as keyof typeof CategoryLabels] ??
          key,
      };
    }),
  [categoryTypes]
);

 const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, () => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const initForm = async () => {
      if (!isAuthReady) return;

      try {
        const userProfile = await apiCall<User>(
          "/users/me",
          {
            method: "GET",
          }
        );

        setCurrentUser(userProfile);

        if (userProfile.role?.name === "super_admin") {
          const allCompanies = await apiCall<Company[]>(
            "/companies",
            {
              method: "GET",
            }
          );

          setCompanies(allCompanies);

          if (allCompanies.length > 0) {
            setSelectedCompanyUuid(
              allCompanies[0].uuid
            );
          }
        } else {
          setSelectedCompanyUuid(
            userProfile.company?.uuid ?? ""
          );
        }
      } catch (error) {
        console.error(
          "Initialization error:",
          error
        );

        toast.error(translations.requirementsError);
      } finally {
        setIsLoading(false);
      }
    };

    initForm();
  }, [translations.requirementsError , isAuthReady]);

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const isSuperAdmin =
      currentUser?.role?.name === "super_admin";

    if (isSuperAdmin && !selectedCompanyUuid) {
      toast.error(translations.selectCompany);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: {
        item_type: string;
        company_uuid?: string;
      } = {
        item_type: itemType,
      };

      if (selectedCompanyUuid) {
        payload.company_uuid = selectedCompanyUuid;
      }

      await apiCall("/categories", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      toast.success(translations.createSuccess);
      navigate("/categories");
    } catch (error) {
      console.error(
        "[AddCategoryForm] Error:",
        error
      );

      const message =
        error instanceof Error
          ? error.message
          : String(error);

      toast.error(
        translations.createError.replace(
          "{message}",
          message
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="add-category-page">
        <Navbar />
        <TopHeader />

        <main className="dashboard-wrapper">
          <div className="add-category-loading">
            <div
              className="add-category-spinner"
              aria-hidden="true"
            />

            <p>{translations.loading}</p>
          </div>
        </main>

        {generateToastContainer()}
      </div>
    );
  }

  const isSuperAdmin =
    currentUser?.role?.name === "super_admin";

  return (
    <div className="add-category-page">
      <Navbar />
      <TopHeader />

      <main className="dashboard-wrapper">
        <div className="add-category-wrapper">
          <form
            className="add-category-card"
            onSubmit={handleSubmit}
          >
            <div className="add-category-header">
              <h1 className="add-category-title">
                {translations.title}
              </h1>

              
            </div>

            <div className="add-category-form">
              {isSuperAdmin && (
                <label className="add-category-field">
                  <span className="add-category-label">
                    {translations.targetCompany}
                  </span>

                  <select
                    className="add-category-input"
                    value={selectedCompanyUuid}
                    onChange={(event) =>
                      setSelectedCompanyUuid(
                        event.target.value
                      )
                    }
                    disabled={isSubmitting}
                    required
                  >
                    {/* {companies.length === 0 && (
                      <option value="">
                        {translations.noCompanies}
                      </option>
                    )} */}

                    {companies.map((company) => (
                      <option
                        key={company.uuid}
                        value={company.uuid}
                      >
                        {company.name}
                        {company.is_platform
                          ? ` (${translations.platform})`
                          : ""}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="add-category-field">
  <span className="add-category-label">
    {translations.categoryType}
  </span>

  <CategorySelect
    options={categoryOptions}
    value={itemType as CategoryItemType}
    onChange={setItemType}
    disabled={isSubmitting}
    className="add-category-input"
  />
</label>
            </div>

            <div className="add-category-actions">
              <button
                type="button"
                className="add-category-cancel"
                onClick={() =>
                  navigate("/categories")
                }
                disabled={isSubmitting}
              >
                {translations.cancel}
              </button>

              <button
                type="submit"
                className="add-category-submit"
                disabled={
                  isSubmitting ||
                  (isSuperAdmin &&
                    !selectedCompanyUuid)
                }
              >
                {isSubmitting
                  ? translations.creating
                  : translations.createCategory}
              </button>
            </div>
          </form>
        </div>
      </main>

      {generateToastContainer()}
    </div>
  );
}

export default AddCategoryForm;
